import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../layout/Layout';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { LogIn, Loader2 } from 'lucide-react';

/**
 * AuthenticatedRoute - Protects routes that require user to be logged in
 * Guests are redirected to home with a ?next=... query param; the home page
 * auto-opens the login modal and navigates back on success. Falls back to a
 * "Login Required" card if for some reason the navigation didn't happen.
 */
const AuthenticatedRoute = ({ children }) => {
  const { user, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // If not logged in (and auth finished loading), auto-redirect to home with
  // ?next set to this URL. Same-origin only — react-router `navigate()` never
  // leaves the origin, so this is safe against open-redirect attacks.
  useEffect(() => {
    if (!authLoading && !user) {
      const nextPath = location.pathname + (location.search || '');
      navigate(`/?next=${encodeURIComponent(nextPath)}`, { replace: true });
    }
  }, [authLoading, user, location.pathname, location.search, navigate]);

  // Show loading while auth is being verified
  if (authLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0A7EA4]" />
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  // Fallback UI shown briefly while the redirect above runs (or if it fails).
  if (!user) {
    const nextPath = location.pathname + (location.search || '');
    return (
      <Layout>
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-8 text-center">
              <LogIn className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-blue-900 mb-2">Login Required</h2>
              <p className="text-blue-800 mb-6">
                Please login to access this page.
              </p>
              <Button
                onClick={() => navigate(`/?next=${encodeURIComponent(nextPath)}`)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="auth-guard-login-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Login / Register
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return children;
};

export default AuthenticatedRoute;
